import {
	Component,
	Input,
	OnInit,
	ViewChild
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { getEventUrl } from 'rev-portal/scheduledEvents/webcast/Constants';

import { AnalyticsService } from 'rev-shared/analytics/Analytics.Service';
import { GridType } from 'rev-shared/analytics/Constant';
import { ILinkCellRendererParams } from 'rev-shared/ui/dataGrid/cellRenderers/LinkCellRenderer.Component';
import { IVbUiDataGridColDef } from 'rev-shared/ui/dataGrid/columns/IVbUiDataGridColDef';
import { ReportGridWrapperComponent } from 'rev-shared/analytics/ReportGridWrapper.Component';
import { VbConfirmationDialogComponent } from 'rev-shared/ui/dialog/VbConfirmationDialogAngular.Component';
import { mediumDateTimeValueFormatter } from 'rev-shared/ui/dataGrid/valueFormatters/MediumDateTimeValueFormatter';

import { EventAnalyticsService } from './EventAnalytics.Service';

import styles from './EventAnalytics.Component.module.less';

enum ViewsColumnPickerGroup {
	EVENT = 'Event',
	METRICS = 'Metrics'
}

@Component({
	selector: 'events-grid',
	templateUrl: './EventsGrid.Component.html',
	host: {
		'[class]': 'styles.eventsGridRoot'
	},
	standalone: false
})
export class EventsGridComponent implements OnInit {
	@Input() public accountId: string;
	@Input() public header: string;
	@Input() public dataFetchContext: { startDate: string, endDate: string };
	@ViewChild(VbConfirmationDialogComponent) public downloadConfirmation: VbConfirmationDialogComponent;
	@ViewChild(ReportGridWrapperComponent, { static: true }) public gridWrapper: ReportGridWrapperComponent;

	public readonly ViewsColumnPickerGroup = ViewsColumnPickerGroup;
	public readonly mediumDateTimeValueFormatter = mediumDateTimeValueFormatter;
	public readonly GridType = GridType;
	public readonly pageSize = 25;
	public readonly styles = styles;
	public readonly titleCellParams: ILinkCellRendererParams<any> = {
		getCellCfg: event => ({
			href: event.canEdit ? '#' + getEventUrl(event.WebcastId) : undefined
		})
	};

	public downloadUrl: string;
	public columnDefs: IVbUiDataGridColDef[];
	constructor(
		public AnalyticsService: AnalyticsService,
		private readonly EventAnalyticsService: EventAnalyticsService,
		private readonly TranslateService: TranslateService,
	){}

	public ngOnInit(): void {
		this.assignColumnDefs();
	}

	public initiateEventsCsvDownload(): void {
		this.EventAnalyticsService.initiateEventsDownload(this.accountId, this.dataFetchContext)
			.then(() => this.downloadConfirmation.open())
			.catch(ee => console.error('error initiating events download', ee));
	}

	private assignColumnDefs(): void {
		this.columnDefs = [
			{
				columnPickerGroup: this.ViewsColumnPickerGroup.EVENT,
				field: 'ScheduledStartDate',
				headerName: this.TranslateService.instant('Reports_Column_ScheduledStartDate'),
				sortable: true,
				tooltipField: 'ScheduledStartDate',
				valueFormatter: this.mediumDateTimeValueFormatter,
				filter: false
			},
			{
				columnPickerGroup: this.ViewsColumnPickerGroup.EVENT,
				field: 'Title',
				headerName: this.TranslateService.instant('Reports_Column_EventTitle'),
				sortable: true,
				tooltipField: 'Title',
				cellRenderer: 'link',
				cellRendererParams: this.titleCellParams
			},
			{
				columnPickerGroup: this.ViewsColumnPickerGroup.METRICS,
				field: 'TotalUsers',
				headerName: this.TranslateService.instant('Reports_Column_Attendees'),
				sortable: true,
				filter: false
			},
			{
				columnPickerGroup: this.ViewsColumnPickerGroup.METRICS,
				field: 'ExperiencedBufferingPercentage',
				headerName: this.TranslateService.instant('Reports_Column_ExperiencedRebuffering'),
				sortable: true,
				filter: false
			},
			{
				columnPickerGroup: this.ViewsColumnPickerGroup.METRICS,
				field: 'AvgExperiencedBufferingDuration',
				headerName: this.TranslateService.instant('Reports_Column_AverageExperiencedRebufferDurationInSecs'),
				sortable: true,
				filter: false
			},
			{
				columnPickerGroup: this.ViewsColumnPickerGroup.METRICS,
				field: 'ExperiencedErrorsOverTotalUsers',
				headerName: this.TranslateService.instant('Reports_Column_ExperiencedErrorsByAttendees'),
				sortable: true,
				filter: false
			},
			{
				columnPickerGroup: this.ViewsColumnPickerGroup.EVENT,
				field: 'ScheduledEndDate',
				headerName: this.TranslateService.instant('Reports_Column_ScheduledEndDate'),
				valueFormatter: this.mediumDateTimeValueFormatter,
				tooltipField: 'ScheduledEndDate',
				sortable: true,
				filter: false,
				hide: true
			},
			{
				columnPickerGroup: this.ViewsColumnPickerGroup.EVENT,
				field: 'AccessControl',
				headerName: this.TranslateService.instant('Reports_Column_AccessControl'),
				sortable: true,
				tooltipField: 'AccessControl',
				cellRenderer: 'highlightText',
				hide: true
			},
			{
				columnPickerGroup: this.ViewsColumnPickerGroup.EVENT,
				field: 'VideoSource',
				headerName: this.TranslateService.instant('Reports_Column_VideoSource'),
				sortable: true,
				tooltipField: 'VideoSource',
				cellRenderer: 'highlightText',
				hide: true
			},
			{
				columnPickerGroup: this.ViewsColumnPickerGroup.EVENT,
				field: 'WebcastType',
				headerName: this.TranslateService.instant('Reports_Column_WebcastType'),
				sortable: true,
				tooltipField: 'WebcastType',
				cellRenderer: 'highlightText',
				hide: true
			},
			{
				columnPickerGroup: this.ViewsColumnPickerGroup.EVENT,
				field: 'Duration',
				headerName: this.TranslateService.instant('Reports_Column_Duration'),
				sortable: true,
				filter: false,
				hide: true
			},
			{
				columnPickerGroup: this.ViewsColumnPickerGroup.METRICS,
				field: 'MulticastErrorsOverTotalUsers',
				headerName: this.TranslateService.instant('Reports_Column_MulticastErrorsByAttendees'),
				sortable: true,
				filter: false,
				hide: true
			}
		];
	}
}
